<template>
	<v-dialog :value="value" max-width="480" @input="$emit('input', $event)">
		<v-card class="kpi-summary-dialog">
			<v-btn
				icon
				small
				class="kpi-summary-close"
				aria-label="Close"
				@click="$emit('input', false)"
			>
				<v-icon size="20">fas fa-times</v-icon>
			</v-btn>

			<v-card-text class="pa-8 pt-10">
				<h2 class="kpi-summary-title text-center mb-1">KPI performance</h2>
				<p class="kpi-summary-subtitle text-center mb-6">
					Latest evaluation from {{ evaluatorName }}
				</p>

				<div class="kpi-summary-metrics mb-4">
					<div
						v-for="metric in metrics"
						:key="metric.key"
						class="kpi-summary-metric"
					>
						<span class="kpi-summary-metric-label">{{ metric.label }}</span>
						<div class="kpi-summary-stars">
							<v-icon
								v-for="star in 5"
								:key="`${metric.key}-${star}`"
								size="18"
								:color="star <= metric.value ? '#f9a825' : '#e0e0e0'"
							>fas fa-star</v-icon>
						</div>
						<span class="kpi-summary-metric-value">({{ metric.value }})</span>
					</div>
				</div>

				<v-divider class="mb-4" />

				<div class="kpi-summary-overall d-flex align-center justify-space-between mb-4">
					<span class="kpi-summary-overall-label">Overall</span>
					<span
						class="kpi-summary-overall-badge"
						:style="{
							backgroundColor: overallStyle.pillBg,
							color: overallStyle.textColor,
							borderColor: overallStyle.borderColor,
						}"
					>{{ overallScore }}</span>
				</div>

				<v-divider v-if="commentItems.length" class="mb-4" />

				<div v-if="commentItems.length">
					<h3 class="kpi-summary-comments-title mb-3">Comments</h3>
					<ul class="kpi-summary-comments mb-0 pl-0">
						<li
							v-for="(comment, index) in commentItems"
							:key="index"
							class="kpi-summary-comment-item"
						>{{ comment }}</li>
					</ul>
				</div>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
import {
	calculateKpiMean,
	getOverallPerformanceStyle,
} from "../utils/supplierKpiOverall"

export default {
	name: "KpiPerformanceSummaryDialog",
	props: {
		value: {
			type: Boolean,
			default: false,
		},
		evaluation: {
			type: Object,
			default: null,
		},
		evaluatorName: {
			type: String,
			default: "",
		},
	},
	computed: {
		metrics() {
			if (!this.evaluation) {
				return []
			}
			return [
				this.buildMetric("deliveryKpi", "Delivery", this.evaluation.deliveryKpi),
				this.buildMetric("qualityKpi", "Quality", this.evaluation.qualityKpi),
				this.buildMetric("supportKpi", "Support", this.evaluation.supportKpi),
			].filter((metric) => metric.value !== null)
		},
		overallScore() {
			if (!this.evaluation) {
				return "—"
			}
			const mean = calculateKpiMean(
				this.evaluation.deliveryKpi,
				this.evaluation.qualityKpi,
				this.evaluation.supportKpi,
			)
			return mean !== null ? mean.toFixed(1) : "—"
		},
		overallStyle() {
			if (!this.evaluation) {
				return {
					pillBg: "#f5f5f5",
					textColor: "#757575",
					borderColor: "#e0e0e0",
				}
			}
			return getOverallPerformanceStyle(
				this.evaluation.deliveryKpi,
				this.evaluation.qualityKpi,
				this.evaluation.supportKpi,
			)
		},
		commentItems() {
			if (!this.evaluation?.comment) {
				return []
			}
			return this.evaluation.comment
				.split(/\r?\n/)
				.map((line) => line.trim())
				.filter(Boolean)
		},
	},
	methods: {
		buildMetric(key, label, value) {
			if (value === null || value === undefined || value === "") {
				return { key, label, value: null }
			}
			const parsed = parseInt(value, 10)
			return {
				key,
				label,
				value: isNaN(parsed) ? null : parsed,
			}
		},
	},
}
</script>

<style scoped>
.kpi-summary-dialog {
	border-radius: 10px;
	position: relative;
}

.kpi-summary-close {
	position: absolute;
	top: 12px;
	right: 12px;
	z-index: 1;
}

.kpi-summary-title {
	font-size: 1.375rem;
	font-weight: 700;
	color: #1a1a1a;
}

.kpi-summary-subtitle {
	color: #757575;
	font-size: 0.9375rem;
}

.kpi-summary-metric {
	display: grid;
	grid-template-columns: 1fr auto auto;
	align-items: center;
	gap: 12px;
	padding: 10px 0;
}

.kpi-summary-metric-label {
	font-weight: 600;
	color: #424242;
}

.kpi-summary-stars {
	display: inline-flex;
	align-items: center;
	gap: 2px;
}

.kpi-summary-metric-value {
	font-weight: 700;
	color: #424242;
	min-width: 28px;
	text-align: right;
}

.kpi-summary-overall-label {
	font-weight: 700;
	font-size: 1rem;
	color: #1a1a1a;
}

.kpi-summary-overall-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 72px;
	padding: 10px 18px;
	border-radius: 10px;
	border: 1.5px solid transparent;
	font-size: 1.25rem;
	font-weight: 800;
}

.kpi-summary-comments-title {
	font-size: 1rem;
	font-weight: 700;
	color: #1a1a1a;
}

.kpi-summary-comments {
	list-style: none;
}

.kpi-summary-comment-item {
	position: relative;
	padding-left: 18px;
	margin-bottom: 8px;
	color: #424242;
	line-height: 1.45;
}

.kpi-summary-comment-item::before {
	content: "";
	position: absolute;
	left: 0;
	top: 0.55em;
	width: 7px;
	height: 7px;
	border-radius: 50%;
	background: #1E7C83;
}
</style>
